import React, { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";

import Container from "react-bootstrap/Container";
import Dropdown from "react-bootstrap/Dropdown";
import Nav from "react-bootstrap/Nav";
import Navbar from "react-bootstrap/Navbar";

import sweapLogo from "./../img/sweap-logo.svg";
import UserAuthService from "../services/UserAuthService";
import ApiService from "../services/ApiService";
import { useTrialMode } from "../hooks/useTrialMode";
import TrialModeModal from "./modals/TrialModeModal";

interface NavigationProps {
    authService: UserAuthService;
    apiService: ApiService;
}

const Navigation: React.FC<NavigationProps> = (props) => {
    const { t } = useTranslation("common");
    const { authService, apiService } = props;
    const [isLoggendIn, setLoggendIn] = useState(authService.isLoggedIn());
    const [showTrialModal, setShowTrialModal] = useState(false);
    const { isTrialMode } = useTrialMode(apiService, authService);
    const [isMenuExpanded, setMenuExpanded] = useState(false);
    const { pathname } = useLocation();

    // close the mobile menu after navigating to another page
    useEffect(() => setMenuExpanded(false), [pathname]);

    useEffect(() => {
        return authService.onAuthStateChanged((user) => {
            setLoggendIn(user !== undefined);
        });
    }, [authService]);

    const handleLogout = () => {
        authService.logout();
    };

    const username = authService.getUser()?.username || "User";
    const welcome = t("navigation.welcome", { username });

    return (
        <Navbar expand="lg" sticky="top" className="app-nav" expanded={isMenuExpanded} onToggle={setMenuExpanded}>
            <Container>
                <div className="d-flex align-items-center gap-2">
                    <Navbar.Brand as={Link} to="/" className="d-flex align-items-center gap-2 me-0">
                        <img alt="Sweap" src={sweapLogo} className="sweap-logo" />
                        <span className="app-nav-title">{t("base.title")}</span>
                    </Navbar.Brand>
                    {isTrialMode && (
                        <button
                            type="button"
                            className="beta-pill"
                            onClick={() => setShowTrialModal(true)}
                            title={t("trialMode.title")}
                        >
                            {t("trialMode.badge")}
                        </button>
                    )}
                </div>

                {isLoggendIn && (
                    <>
                        <Navbar.Toggle aria-controls="basic-navbar-nav" />
                        <Navbar.Collapse id="basic-navbar-nav">
                            <Nav className="me-auto ms-lg-4">
                                <NavLink className="nav-link" to="/" end>
                                    {t("navigation.home")}
                                </NavLink>
                                <NavLink className="nav-link" to="/documents">
                                    {t("navigation.documents")}
                                </NavLink>
                                <NavLink className="nav-link" to="/templates">
                                    {t("navigation.templates")}
                                </NavLink>
                            </Nav>

                            {/* mobile: user entries at the end of the collapsed menu */}
                            <Nav className="d-lg-none border-top">
                                <span className="navbar-text">{welcome}</span>
                                <Nav.Link as="button" className="text-start text-danger" onClick={handleLogout}>
                                    {t("navigation.logout")}
                                </Nav.Link>
                            </Nav>

                            <Dropdown align="end" className="d-none d-lg-block">
                                <Dropdown.Toggle
                                    as="button"
                                    className="avatar-button"
                                    aria-label={t("navigation.userMenu")}
                                >
                                    {username.slice(0, 2).toUpperCase()}
                                </Dropdown.Toggle>
                                <Dropdown.Menu>
                                    <Dropdown.Header>{welcome}</Dropdown.Header>
                                    <Dropdown.Item as="button" className="text-danger" onClick={handleLogout}>
                                        {t("navigation.logout")}
                                    </Dropdown.Item>
                                </Dropdown.Menu>
                            </Dropdown>
                        </Navbar.Collapse>
                    </>
                )}
            </Container>

            <TrialModeModal show={showTrialModal} onHide={() => setShowTrialModal(false)} />
        </Navbar>
    );
};

export default Navigation;
